<template>
  <div>
    <!-- TÍTULO -->
    <h4 class="mb-0">Reporting List</h4>

    <!-- CARD 2: FILTROS -->
    <b-card-code title="Filtros">
      <b-card-body>
        <div class="d-flex flex-wrap justify-content-start">
          <!-- Ordenamiento -->
          <b-form-group label-for="sortBySelect" class="mr-3">
            <h5>Ordenar</h5>
            <b-input-group>
              <b-form-select id="sortBySelect" v-model="sortBy" :options="sortOptions">
                <template #first>
                  <option value="">Ninguno</option>
                </template>
              </b-form-select>

              <b-form-select v-model="sortDesc" :disabled="!sortBy">
                <option :value="false">Asc</option>
                <option :value="true">Desc</option>
              </b-form-select>
            </b-input-group>
          </b-form-group>

          <!-- Filtros normales -->
          <b-form-group v-for="filter in filtersConfig.filter(f => f.type !== 'date-range')" :key="filter.key"
            class="mr-3">
            <h5>{{ filter.label }}</h5>
            <b-input-group>
              <b-form-input v-model="filters[filter.key]" :type="filter.type || 'search'"
                :placeholder="filter.placeholder" @keyup.enter="applyFilter" />
            </b-input-group>
          </b-form-group>

          <!-- Selector de puertas permitidas (multiple) -->
          <b-form-group class="mr-3" style="min-width: 360px;">
            <h5>Puertas</h5>
            <v-select v-model="selectedDoors" :options="doorOptions" label="label" :reduce="o => o.value" multiple
              :clearable="true" :disabled="isLoadingDoors" placeholder="Selecciona puertas..." />
            <small v-if="isLoadingDoors" class="text-muted">Cargando puertas...</small>
            <small v-else-if="!doorOptions.length" class="text-muted">No hay puertas disponibles</small>
          </b-form-group>

          <!-- Filtro de rango de fechas -->
          <b-form-group v-for="filter in filtersConfig.filter(f => f.type === 'date-range')" :key="filter.key"
            class="mr-3">
            <h5>{{ filter.label }}</h5>
            <b-input-group>
              <b-form-input v-model="filters.startDateFilter" type="date" placeholder="Fecha inicio" class="mr-2" />
              <b-form-input v-model="filters.endDateFilter" type="date" placeholder="Fecha fin" class="ml-2" />
            </b-input-group>
          </b-form-group>

          <!-- Botones -->
          <b-form-group class="mt-auto">
            <b-button variant="primary" class="mt-2 mr-2" @click="applyFilter">
              <feather-icon icon="SearchIcon" class="mr-1" /> Buscar
            </b-button>
            <b-button variant="outline-secondary" class="mt-2" @click="clearAllFilters">
              <feather-icon icon="XIcon" class="mr-1" /> Limpiar
            </b-button>
          </b-form-group>
        </div>
      </b-card-body>
    </b-card-code>

    <!-- CARD 3: TABLA -->
    <b-card-code title="Resultados">
     <b-table borderless striped hover responsive class="position-relative" :items="items" :fields="fields"
        :sort-by.sync="sortBy" :sort-desc.sync="sortDesc">
        <template #cell(accreditation_status_permit)="data">
          <b-badge :variant="activeLabel[1][data.value]">
            {{ activeLabel[0][data.value] }}
          </b-badge>
        </template>

        <template #cell(way)="data">
          <div class="text-center">
            <img v-if="data.item.way && data.item.way.toUpperCase() === 'E'" src="@/assets/images/icons/entrance.png"
              alt="Puerta abierta" title="IN" style="height: 30px" />
            <img v-else-if="data.item.way && data.item.way.toUpperCase() === 'S'" src="@/assets/images/icons/exit.png"
              alt="Puerta cerrada" title="OUT" style="height: 28px" />
            <span>
              {{
                data.item.way && data.item.way.toUpperCase() === "E"
                  ? "IN"
                  : data.item.way && data.item.way.toUpperCase() === "S"
                    ? "OUT"
                    : ""
              }}
            </span>
          </div>
        </template>

        <template #cell(reading_method)="data">
          <div class="d-flex align-items-center">
            <feather-icon v-if="String(data.value || '').toLowerCase() === 'carplate'" icon="VideoIcon" size="16"
              class="mr-50" />
            <feather-icon v-else-if="String(data.value || '').toUpperCase() === 'PDA'" icon="SmartphoneIcon" size="16"
              class="mr-50" />

            <span>
              {{
                String(data.value || '').toLowerCase() === 'carplate'
                  ? 'Matrícula'
                  : String(data.value || '').toUpperCase() === 'PDA'
                    ? 'PDA'
                    : data.value || ''
              }}
            </span>
          </div>
        </template>
      </b-table>

      <!-- Paginación -->
      <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
        <b-form-group label="Por página" label-cols="6" label-align="left" label-size="sm"
          class="text-nowrap mb-md-0 mr-1">
          <b-form-select v-model="perPage" size="sm" inline :options="pageOptions" @change="onPerPageChange" />
        </b-form-group>

        <div>
          <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" @input="onPageChange">
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </b-card-body>
    </b-card-code>
  </div>
</template>

<script>
import axios from "@/libs/axios";
import BCardCode from "@core/components/b-card-code/BCardCode.vue";
import {
  BTable,
  BBadge,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BCardBody,
  BButton,
  BInputGroupAppend,
} from "bootstrap-vue";
import moment from "moment";
import { decryptString } from "@/libs/crypto";

import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  components: {
    BCardCode,
    BTable,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BCardBody,
    BButton,
    BInputGroupAppend,
    BBadge,
    vSelect,
  },

  data() {
    return {
      items: [],
      isFetching: false,

      // paginación
      perPage: 50,
      pageOptions: [50, 100, 200, 500],
      totalRows: 0,
      currentPage: 1,

      // sorting
      sortBy: "",
      sortDesc: false,

      // filtros
      filters: {
        nameFilter: "",
        dniFilter: "",
        matriculaFilter: "",
        startDateFilter: null,
        endDateFilter: null,
      },

      // puertas permitidas
      isLoadingDoors: false,
      doorOptions: [],
      selectedDoors: [],

      //  mapa ID_Aforo -> Aforo_name 
      aforoNameById: {},

      filtersConfig: [
        { key: "nameFilter", label: "Nombre", placeholder: "Busqueda por nombre", param: "Nom_titular" },
        { key: "dniFilter", label: "DNI", placeholder: "Busqueda por dni", param: "dni" },
        { key: "matriculaFilter", label: "Matricula", placeholder: "Busqueda por matricula", param: "Matricula" },
        {
          key: "startDateFilter",
          label: "Fecha",
          type: "date-range",
          param: "datetime_start_acreditation",
          formatter: (value) => (value ? moment(value).format("YYYY-MM-DD") : null),
        },
      ],

      fields: [
        { key: "accreditation_status_permit", label: "", sortable: true },
        {
          key: "puerta_aforo",
          label: "Puerta",
          sortable: false,
          formatter: (value, key, item) => `${item.Aforo_Name} - ${item.Name}`,
        },
        { key: "way", label: "", sortable: true },
        { key: "code", label: "Codigo", sortable: true },
        { key: "Nom_titular", label: "Nombre", sortable: true },
        { key: "Cognom1_titular", label: "Apellido", sortable: true },
        { key: "reading_method", label: "Método", sortable: true },
        { key: "ShortName", label: "Tipo", sortable: false },
        { key: "Empresa_nom", label: "Empresa", sortable: true },
        {
          key: "timestamp_pda",
          label: "Fecha-hora",
          sortable: true,
          formatter: (value) => (value ? moment(value).utc().format("DD/MM/YYYY HH:mm") : ""),
        },
        { key: "event_name", label: "EVENTO", sortable: true },
      ],

      activeLabel: [
        { 0: "Denied", 1: "Permitted" },
        { 0: "light-danger", 1: "light-success" },
      ],
    };
  },

  computed: {
    sortOptions() {
      return this.fields.filter((f) => f.sortable).map((f) => ({ text: f.label, value: f.key }));
    },
  },

  methods: {
    getAllowedAforoIds() {
      const raw = decryptString(localStorage.getItem("Aforo_permit")) || "";
      return raw
        .split(",")
        .map((x) => x.trim())
        .filter((x) => x.length > 0);
    },

    //  cargar mapa ID_Aforo -> Aforo_name
    async loadAforoMap() {
      try {
        const allowedIds = this.getAllowedAforoIds();
        if (!allowedIds.length) {
          this.aforoNameById = {};
          return;
        }

        const res = await axios.get("/aforo", { params: { Active: 1 } });

        const map = {};
        (res.data || []).forEach((a) => {
          const id = String(a.ID_Aforo);
          if (!allowedIds.includes(id)) return;

          map[id] = a.Aforo_name || a.Aforo_Name || a.AforoName || id;
        });

        this.aforoNameById = map;
      } catch (e) {
        console.error("Error cargando aforos (/aforo)", e);
        this.aforoNameById = {};
      }
    },

    // cargar puertas permitidas y pintar label con Aforo_name del mapa
    async loadDoors() {
      this.isLoadingDoors = true;
      try {
        const allowedIds = this.getAllowedAforoIds();
        if (!allowedIds.length) {
          this.doorOptions = [];
          this.selectedDoors = [];
          return;
        }

        // GET /door?ID_Aforo=1,2,3
        const ID_Aforo = allowedIds.join(",");
        const res = await axios.get("/door", { params: { ID_Aforo } });

        const doors = (res.data || []).slice().sort((a, b) =>
          String(a.Name || "").localeCompare(String(b.Name || ""))
        );

        this.doorOptions = doors.map((d) => {
          const aforoName = this.aforoNameById[String(d.ID_Aforo)] || String(d.ID_Aforo);
          return {
            label: `${aforoName} - ${d.Name}`,
            value: d.pkid_door,
          };
        });
      } catch (e) {
        console.error("Error cargando puertas (/door)", e);
        this.doorOptions = [];
        this.selectedDoors = [];
      } finally {
        this.isLoadingDoors = false;
      }
    },

    async fetchData() {
      this.isFetching = true;
      try {
        const params = { page: this.currentPage, limit: this.perPage };

        // filtros normales
        this.filtersConfig.forEach((filter) => {
          if (filter.type === "date-range") return;
          const raw = this.filters[filter.key];
          if (raw) params[filter.param] = raw;
        });

        // fechas
        if (this.filters.startDateFilter) {
          params.datetime_start_acreditation = moment(this.filters.startDateFilter).format("YYYY-MM-DD");
        }
        if (this.filters.endDateFilter) {
          params.datetime_end_acreditation = moment(this.filters.endDateFilter).format("YYYY-MM-DD");
        }

        // aforos permitidos (tal cual lo esperas en backend)
        params.ID_Aforo = decryptString(localStorage.getItem("Aforo_permit"));

        // puertas seleccionadas
        if (this.selectedDoors && this.selectedDoors.length > 0) {
          params.fkid_door = this.selectedDoors.join(",");
        }

        const response = await axios.get("/reporting", { params });

        if (response.data && response.data.data) {
          this.items = response.data.data.map((item) => {
            if (item.timestamp) {
              item.timestamp = `0x${Buffer.from(item.timestamp).toString("hex").toUpperCase()}`;
            }
            return item;
          });
          this.totalRows = response.data.totalCount || 0;
        } else {
          this.items = [];
          this.totalRows = 0;
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        this.items = [];
        this.totalRows = 0;
      } finally {
        this.isFetching = false;
      }
    },

    applyFilter() {
      this.currentPage = 1;
      this.fetchData();
    },

    clearAllFilters() {
      this.filters.nameFilter = "";
      this.filters.dniFilter = "";
      this.filters.matriculaFilter = "";
      this.filters.startDateFilter = null;
      this.filters.endDateFilter = null;
      this.selectedDoors = [];
      this.applyFilter();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchData();
    },

    onPerPageChange() {
      this.currentPage = 1;
      this.fetchData();
    },
  },

  async created() {
    //  1) cargar mapa de aforos
    await this.loadAforoMap();
    // 2) cargar puertas usando el mapa para mostrar el nombre
    await this.loadDoors();
    // 3) cargar reporting
    this.fetchData();
  },

  mounted() {
    if (!localStorage.getItem("accessToken")) {
      this.$router.go(0);
    }
  },
};
</script>

<style>
.colStyle {
  font-size: small;
}

/* vue-select dark mode */
.dark-layout .v-select .vs__dropdown-toggle {
  background: transparent !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  color: #d0d2d6 !important;
}

.dark-layout .v-select .vs__search,
.dark-layout .v-select .vs__search:focus,
.dark-layout .v-select .vs__selected {
  color: #d0d2d6 !important;
}

.dark-layout .v-select .vs__open-indicator,
.dark-layout .v-select .vs__clear {
  fill: #d0d2d6 !important;
}

.dark-layout .v-select .vs__dropdown-menu {
  background: #283046 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.dark-layout .v-select .vs__dropdown-option {
  color: #d0d2d6 !important;
}

.dark-layout .v-select .vs__dropdown-option--highlight {
  background: rgba(115, 103, 240, 0.25) !important;
  color: #fff !important;
}

.dark-layout .v-select .vs__selected-options .vs__selected {
  background: rgba(115, 103, 240, 0.15) !important;
  border: 1px solid rgba(115, 103, 240, 0.35) !important;
  color: #fff !important;
}

.dark-layout .v-select .vs__search::placeholder {
  color: rgba(208, 210, 214, 0.6) !important;
}
</style>